import { defineStore } from 'pinia'
import { ref, computed, watch, type ComputedRef } from 'vue'
import { collection, onSnapshot, getDocs, query, where } from 'firebase/firestore'
import { firestore } from '@/firebase'
import { useAuthStore } from './auth'
import createLogger from '@/utils/logger'
import {
  classSchema,
  classScheduleSchema,
  type Class,
  type ClassId,
  type ClassSchedule,
} from '@gh/common/models'

const logger = createLogger('stores/classes.ts')

type ClassWithSchedules = Class & { schedules: ClassSchedule[] }

export const useClassesStore = defineStore('classes', () => {
  const classes = ref<ClassWithSchedules[]>([])
  const classesWithTestClasses = ref<ClassWithSchedules[]>([])
  const isLoading = ref(true)
  const authStore = useAuthStore()
  const unsubscribeAuth: (() => void) | null = null
  let unsubscribeClassesSnapshot: (() => void) | null = null
  const isLoadingSchedules = ref(false)

  const authUser = computed(() => authStore.user)
  const isTestCustomer = computed(() => authStore.isTestCustomer)

  watch(
    authUser,
    async (newVal) => {
      if (newVal) {
        await retrieveClassesFromFirestore()
      } else {
        if (unsubscribeClassesSnapshot) {
          unsubscribeClassesSnapshot()
        }
        classes.value = []
      }
    },
    { immediate: true },
  )

  async function retrieveClassesFromFirestore() {
    logger.log('retrieveClassesFromFirestore')
    if (unsubscribeClassesSnapshot) {
      unsubscribeClassesSnapshot()
    }
    unsubscribeClassesSnapshot = onSnapshot(collection(firestore, 'classes'), async (docSnap) => {
      isLoading.value = true
      const rawClasses: Class[] = []

      docSnap.forEach((doc) => {
        const {
          success,
          data: classData,
          error,
        } = classSchema.safeParse({
          ...doc.data(),
          id: doc.id,
        })
        if (!success) {
          logger.error('Error parsing class:', error)
          return
        }

        rawClasses.push(classData)
      })

      // Fetch schedules for all classes and attach them
      try {
        isLoadingSchedules.value = true
        const now = new Date()
        const schedulesRef = collection(firestore, 'class_schedules')

        const ongoingQuery = query(schedulesRef, where('scheduleEndDateUtc', '==', null))

        const futureQuery = query(schedulesRef, where('scheduleEndDateUtc', '>', now))

        const [ongoingSnapshot, futureSnapshot] = await Promise.all([
          getDocs(ongoingQuery),
          getDocs(futureQuery),
        ])

        const schedulesByClassId = new Map<ClassId, ClassSchedule[]>()

        const parseAndAddSchedule = (data: unknown, id: string) => {
          const result = classScheduleSchema.safeParse({
            scheduleTimeZone: 'UTC',
            ...(data as Record<string, unknown>),
            id,
          })
          if (!result.success) {
            logger.error('Error parsing class schedule:', result.error)
            return
          }
          const schedule = result.data
          const list = schedulesByClassId.get(schedule.classId) ?? []
          list.push(schedule)
          schedulesByClassId.set(schedule.classId, list)
        }

        ongoingSnapshot.forEach((doc) => {
          parseAndAddSchedule(doc.data(), doc.id)
        })

        futureSnapshot.forEach((doc) => {
          parseAndAddSchedule(doc.data(), doc.id)
        })

        const withSchedules: ClassWithSchedules[] = rawClasses.map((classData) => {
          const schedules = schedulesByClassId.get(classData.id) ?? []
          const sortedSchedules = [...schedules].sort((a, b) => {
            if (a.dayOfWeek !== b.dayOfWeek) return a.dayOfWeek - b.dayOfWeek
            if (a.scheduleHour !== b.scheduleHour) return a.scheduleHour - b.scheduleHour
            return a.scheduleMinute - b.scheduleMinute
          })
          return {
            ...classData,
            schedules: sortedSchedules,
            activeSchedules: sortedSchedules.filter(
              (schedule) =>
                schedule.scheduleEndDateUtc === null ||
                schedule.scheduleEndDateUtc === undefined ||
                schedule.scheduleEndDateUtc > new Date(),
            ),
          }
        })

        classesWithTestClasses.value = withSchedules
      } catch (error) {
        logger.error('Error fetching class schedules:', error)
        // Fallback to classes without schedules if schedule fetch fails
        classesWithTestClasses.value = rawClasses.map((classData) => ({
          ...classData,
          schedules: [],
        }))
      } finally {
        isLoadingSchedules.value = false
      }
    })
  }

  watch([classesWithTestClasses, isTestCustomer], ([list, isTestCustomer]) => {
    if (isTestCustomer) {
      classes.value = list
    } else {
      classes.value = list.filter((classData) => !classData.testClass)
    }
    isLoading.value = false
  })

  const classesMap: ComputedRef<Map<ClassId, ClassWithSchedules>> = computed(() => {
    return new Map(classes.value.map((classData) => [classData.id, classData]))
  })

  const classesWithTestClassesMap: ComputedRef<Map<ClassId, ClassWithSchedules>> = computed(() => {
    return new Map(classesWithTestClasses.value.map((classData) => [classData.id, classData]))
  })

  function onUnmounted() {
    if (unsubscribeAuth) {
      unsubscribeAuth()
    }
    if (unsubscribeClassesSnapshot) {
      unsubscribeClassesSnapshot()
    }
  }

  function signOut() {
    if (unsubscribeClassesSnapshot) {
      unsubscribeClassesSnapshot()
    }
    classes.value = []
  }

  return {
    classes,
    classesWithTestClasses,
    isLoading,
    isLoadingSchedules,
    onUnmounted,
    retrieveClassesFromFirestore,
    signOut,
    classesMap,
    classesWithTestClassesMap,
  }
})
